import { LineItem, User } from '../../core';
import { getEffectiveLineItems } from '../../core/lineItem/utils';
import { SalesTermsMapper } from '../../mappers';
import {
  AccountLevel,
  BusinessEntity,
  Contact,
  CustomFields,
  Expense,
  LineItem as LineItemType,
  ListTemplate,
  Membership,
  Note,
  Organization,
  Place,
  SalesOrderStatus,
  SalesTerms,
  Shipment,
} from '../../types';
import Money from '../../utils/Money';
import { AddressComponent } from '../components/address.template';
import { BusinessEntityComponent, BusinessEntityTemplateType } from '../components/businessEntity.template';
import { documentNotes } from '../components/documentNotes.template';
import { ExpensesComponent, ExpenseTemplateDataType } from '../components/expensesGrid.template';
import { OrganizationComponent } from '../components/organization.template';
import { getProductAttributes, ProductsComponent, ProductTemplateDataType } from '../components/productsGrid.template';
import { ShipmentComponent, ShipmentTemplateType } from '../components/shipment.template';
import { DocumentTypePopulator } from '../DocumentGenerator';
import { applyListMeta, formatCurrency, formatDate, getPrimaryBusiness } from './formatters';
import { PurchaseOrderInputType } from './purchaseOrder.template';

// hardcoded until material inventory is added in
const MaterialInventoryProductIDs = {
  // Millennium Pacific
  4284: 'Pallet CHEP',
};

export type SalesOrderOptions = {
  /**
   * Allow document type to set preferred units for total calculation
   * @deprecated
   */
  totalUnitsType: 'ordered' | 'picked';
  productDescriptionTemplate?: string;
};

export type SalesOrderInputType = {
  organization: Organization;
  slug: string;
  shipDate: string | Date;
  deliveryDate: string | Date;
  salesPerson: Membership;
  subsidiary: Place;
  salesPersonId: number;
  businessEntity?: BusinessEntity;
  businessEntityId?: number;
  businessEntityContactId?: number;
  businessEntityContact?: Contact;
  currency?: string;
  salesTerms: SalesTerms;
  status: SalesOrderStatus;
  customerPo: string;
  billToId: number;
  billTo: Place;
  currencyCode?: string;
  customFields: CustomFields;
  shipment: Shipment;
  lineItems: LineItemType[];
  expenses: Expense[];
  submittedAt: string | Date;
  notes: Note[];
  fulfilledByPurchaseOrders: Pick<PurchaseOrderInputType, 'shipment' | 'lineItems' | 'customFields' | 'vendorPo'>[];
};

type PickUpTemplateDataType = {
  address: Record<string, string | undefined | boolean>;
  products: ProductTemplateDataType;
  stop: number;
  orderType: string;
  purchaseOrder?: { customFields: CustomFields; vendorSo: string };
};

export type SalesOrderTemplateDataType = Partial<{
  orderNumber: string;
  status: string;
  salesPerson: string;
  customer: BusinessEntityTemplateType;
  shipment: ShipmentTemplateType;
  pickUps: ListTemplate<PickUpTemplateDataType>;
  phoneNumber: string;
  email: string;
  salesTerms: string;
  freightCharges: ExpenseTemplateDataType;
  charges: ExpenseTemplateDataType;
  products: ProductTemplateDataType;
  fulfilledByPurchaseOrders: boolean;
  customFields: CustomFields;
  billToAddress: Record<string, string | undefined | boolean>;
  totalWeight: string;
  productsSubtotal: string;
  chargesSubtotal: string;
  orderTotal: string;
  customerPo: string;
  orderDate: string;
  notes: ListTemplate<{ text: string }>;
  fax?: string;
  billTo: Record<string, string | undefined | boolean>;
  freightChargesSubtotal: string;
  businessAddress: Record<string, string | undefined | boolean>;
}>;

export const SalesOrderTemplate: DocumentTypePopulator<
  SalesOrderInputType,
  SalesOrderOptions,
  SalesOrderTemplateDataType
> = {
  populate: (salesOrder, { type, templateId, productDescriptionTemplate }, globalOptions) => {
    if (!salesOrder) {
      return {};
    }

    const {
      slug,
      status,
      organization,
      subsidiary: orderSubsidiary,
      salesPerson,
      shipment,
      businessEntity,
      shipDate,
      deliveryDate,
      currencyCode,
      lineItems,
      billTo,
      customFields,
      expenses,
      salesTerms,
      customerPo,
      submittedAt,
      notes,
      fulfilledByPurchaseOrders,
    } = salesOrder;

    const { freightCompany } = shipment;

    const nonMaterialInventoryLineItems = getEffectiveLineItems(
      lineItems.filter((li) => !li?.product?.id || !(li.product.id in MaterialInventoryProductIDs))
    );

    const mergedLineItems = LineItem.Utils.flattenForDisplay(nonMaterialInventoryLineItems);

    const arExpenses = expenses.filter((expense) => {
      const arExpense = expense.accountLevel === AccountLevel.ACCOUNTS_RECEIVABLE;
      const orderExpense = expense.businessEntity?.id === businessEntity?.id;
      const notDlv = !expense.includedInDlvPrice;

      return arExpense && orderExpense && notDlv;
    });

    const apExpenses = expenses.filter((expense) => {
      return expense.accountLevel === AccountLevel.ACCOUNTS_PAYABLE;
    });

    const freightExpenses = expenses.filter((expense) => {
      const apExpense = expense.accountLevel === AccountLevel.ACCOUNTS_PAYABLE;
      const freightExpense = expense.businessEntity?.id === freightCompany?.id;

      return apExpense && freightExpense;
    });

    const arCharges = arExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);
    const apCharges = apExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);

    const freightExpensesSubtotal = freightExpenses.reduce((acc, expense) => {
      return acc + Money.toDollars(expense.currencyAmount ?? expense.unitAmount) * expense.quantity;
    }, 0);

    const primaryBusiness = getPrimaryBusiness(organization);
    const salesOrderLineItems = mergedLineItems.filter((li) => li.purchaseOrderId === null);

    const pickups: PickUpTemplateDataType[] =
      fulfilledByPurchaseOrders?.map((po, i) => ({
        address: AddressComponent.templatize(po.shipment.place),
        products: ProductsComponent.templatize(po.lineItems, {
          currency: currencyCode,
          descriptionTemplate: productDescriptionTemplate ?? organization?.productDescriptionTemplate,
        }),
        orderType: 'Purchase Order',
        purchaseOrder: { customFields: po.customFields, vendorSo: po.vendorPo },
        stop: i + 1,
      })) ?? [];

    if (salesOrderLineItems.length > 0) {
      pickups.push({
        orderType: 'Sales Order',
        address: AddressComponent.templatize(shipment.origin),
        products: ProductsComponent.templatize(salesOrderLineItems as any, {
          currency: currencyCode,
          descriptionTemplate: productDescriptionTemplate ?? organization?.productDescriptionTemplate,
        }),
        stop: pickups.length + 1,
      });
    }

    const subsidiary = orderSubsidiary ?? salesPerson?.subsidiary;

    return {
      orderNumber: slug,

      orgLogo: organization?.logoUrl,
      organization: OrganizationComponent.templatize({
        organization,
        subsidiary,
        customOrgName: globalOptions.customOrgName,
      }),
      subsidiary: subsidiary?.description,
      status: status as unknown as string,
      orderDate: formatDate(submittedAt),
      salesPerson: User.formatName(salesPerson?.user),
      customer: BusinessEntityComponent.templatize(businessEntity),
      soPrefix: organization?.salesOrderDocumentPrefix,
      salesTerms: SalesTermsMapper.getLabel(salesTerms),
      customFields,
      customerPo,
      phoneNumber: primaryBusiness?.phone ?? shipment.origin?.phone,
      email: salesPerson?.user?.email,
      businessAddress: AddressComponent.templatize(subsidiary ?? primaryBusiness),
      fax: primaryBusiness?.faxNumber,

      billTo: AddressComponent.templatize(billTo),

      shipment: ShipmentComponent.templatize({ ...shipment, destination: shipment.place, deliveryDate, shipDate }),

      // shows ALL products. (SO & PO)
      products: ProductsComponent.templatize(mergedLineItems as any, {
        currency: currencyCode,
        descriptionTemplate: productDescriptionTemplate ?? organization?.productDescriptionTemplate,
      }),

      noPurchaseOrders: fulfilledByPurchaseOrders?.length === 0,
      hasPurchaseOrders: fulfilledByPurchaseOrders?.length >= 1,
      pickUps: applyListMeta(pickups),

      notes: documentNotes(notes, templateId, type),

      arCharges: ExpensesComponent.templatize(arExpenses, { currency: currencyCode }),
      apCharges: ExpensesComponent.templatize(apExpenses, { currency: currencyCode }),
      freightCharges: ExpensesComponent.templatize(freightExpenses, { currency: currencyCode }),

      // computed values
      arChargesSubtotal: formatCurrency(arCharges, currencyCode),
      apChargesSubtotal: formatCurrency(apCharges, currencyCode),
      freightChargesSubtotal: formatCurrency(freightExpensesSubtotal, currencyCode),
      ...getProductAttributes(nonMaterialInventoryLineItems as LineItemType[], apCharges, arCharges),
    };
  },
  definitionMap: {
    orderNumber: { label: 'Order Number', type: 'string' },
    status: { label: 'Status', type: 'string' },
    salesPerson: { label: 'Sales Person', type: 'string' },
    customer: {
      label: 'Customer',
      type: 'object',
      children: { primaryBusiness: { label: 'Primary Business', type: 'string' } },
    },
    shipment: {
      label: 'Shipment',
      type: 'object',
      children: ShipmentComponent.definitionMap({ customFieldsRoot: 'salesOrder' }),
    },
    products: { label: 'Products', type: 'table', children: ProductsComponent.definitionMap },
    customFields: { label: 'Custom Fields', type: 'customFields', root: 'salesOrder' },
    billToAddress: { label: 'Bill To Address', type: 'address', children: AddressComponent.definitionMap },
    fulfilledByPurchaseOrders: { label: 'Fulfilled By Purchase Orders', type: 'boolean' },
    totalWeight: { label: 'Total Weight', type: 'string' },
    productsSubtotal: { label: 'Products Subtotal', type: 'string' },
    chargesSubtotal: { label: 'Charges Subtotal', type: 'string' },
    orderTotal: { label: 'Order Total', type: 'string' },
    customerPo: { label: 'Customer PO', type: 'string' },
    orderDate: { label: 'Order Date', type: 'string' },
    notes: { label: 'Notes', type: 'list', children: { text: { label: 'Text', type: 'string' } } },
    phoneNumber: { label: 'Phone Number', type: 'string' },
    email: { label: 'Email', type: 'string' },
    salesTerms: { label: 'Sales Terms', type: 'string' },
    freightCharges: { label: 'Freight Charges', type: 'table', children: ExpensesComponent.definitionMap },
    freightChargesSubtotal: { label: 'Freight Charges Subtotal', type: 'string' },
    billTo: { label: 'Bill To', type: 'address', children: AddressComponent.definitionMap },

    fax: { label: 'Fax', type: 'string' },
    businessAddress: { label: 'Business Address', type: 'address', children: AddressComponent.definitionMap },
    pickUps: {
      label: 'Pick Ups',
      type: 'list',
      children: {
        address: { label: 'Address', type: 'address', children: AddressComponent.definitionMap },
        stop: { label: 'Stop', type: 'number' },
        products: { label: 'Products', type: 'table', children: ProductsComponent.definitionMap },
        orderType: { label: 'Order Type', type: 'string' }, // allow dynamic display based on whether it's a PO or SO
        purchaseOrder: {
          label: 'Order',
          type: 'object',
          children: {
            customFields: { label: 'Custom Fields', type: 'customFields', root: 'purchaseOrder' },
            vendorSo: { label: 'Vendor SO', type: 'string' },
          },
        },
      },
    },
    charges: { label: 'Charges', type: 'table', children: ExpensesComponent.definitionMap },
  },
};
